import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check, X, Eye, AlertTriangle, Zap, FileText, Image, Table, Brain, ArrowRight, Calendar, RefreshCw, ClipboardList, Mail, Loader2, Play, Clock } from 'lucide-react';
import { CEO_BRIEFING } from '../../data/ceoBriefing';
import { EMAIL_REPLIES, INCOMING_EMAIL_REPLIES } from '../../data/emailReplyData';
import { RippledTaskCard, RippledInboxItem, RippledApprovalCard, RippledRiskCard } from './V4BriefingShared';
import V4TaskExecution from './V4TaskExecution';
import V4ExecutionView from './V4ExecutionView';
import V4RiskBriefing from './V4RiskBriefing';
import V4EmailReply from './V4EmailReply';
import { R1_EXECUTION, R1_SHARE_DETAILS, R1_REQUEST_CHANGE_RESPONSES, R1_CHANGE_MESSAGES, R2_EXECUTION, R2_SHARE_DETAILS, R2_REQUEST_CHANGE_RESPONSES, R2_CHANGE_MESSAGES } from '../../data/executionData';

const AGENT_THUMBS = {
  luc: '/Thumbnails/Luc.png',
  marsi: '/Thumbnails/Marsi.png',
  sally: '/Thumbnails/Sally.png',
  buzz: '/Thumbnails/Buzz.png',
  wright: '/Thumbnails/Wright.png',
  zira: '/Thumbnails/Ziira.png',
  finn: '/Thumbnails/Finn.png',
  dora: '/Thumbnails/Dora.png',
  stark: '/Thumbnails/Stark.png',
  tali: '/Thumbnails/Tali.png',
};

const URGENCY_STYLES = {
  'time-sensitive': { label: 'Time Sensitive', dark: 'text-amber-400 bg-amber-400/10', light: 'text-amber-700 bg-amber-700/10' },
  'due-today': { label: 'Due Today', dark: 'text-orange-400 bg-orange-400/10', light: 'text-orange-700 bg-orange-700/10' },
  'critical': { label: 'Critical', dark: 'text-red-400 bg-red-400/10', light: 'text-red-600 bg-red-600/10' },
};

const ARTIFACT_ICONS = { pdf: FileText, image: Image, spreadsheet: Table };

const MEETING_STATUS_LABELS = {
  reminded: { label: 'Starting soon', colorDark: 'text-amber-400 bg-amber-400/10', colorLight: 'text-amber-600 bg-amber-600/10' },
  'in-progress': { label: 'In progress', colorDark: 'text-indigo-400 bg-indigo-400/10', colorLight: 'text-indigo-600 bg-indigo-600/10' },
  completed: { label: 'Completed', colorDark: 'text-emerald-400 bg-emerald-400/10', colorLight: 'text-emerald-600 bg-emerald-600/10' },
};

const SectionLabel = ({ icon: Icon, title, count, isDark, accent }) => {
  const accentColor = accent === 'red'
    ? (isDark ? 'text-red-400' : 'text-red-500')
    : accent === 'amber'
      ? (isDark ? 'text-amber-400' : 'text-amber-600')
      : (isDark ? 'text-white/50' : 'text-slate-500');
  return (
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className={`w-3.5 h-3.5 ${accentColor}`} />}
      <span className={`text-[11px] font-semibold uppercase tracking-wider ${accentColor}`}>{title}</span>
      {count != null && (
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/40' : 'bg-black/[0.04] text-slate-500'}`}>{count}</span>
      )}
    </div>
  );
};

const UrgencyBadge = ({ urgency, isDark }) => {
  const style = URGENCY_STYLES[urgency];
  if (!style) return null;
  return <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? style.dark : style.light}`}>{style.label}</span>;
};

const ArtifactChip = ({ artifact, isDark }) => {
  const Icon = ARTIFACT_ICONS[artifact.type] || FileText;
  return (
    <button className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-white/[0.06] text-white/60 hover:bg-white/[0.1]' : 'bg-black/[0.04] text-slate-500 hover:bg-black/[0.08]'}`}>
      <Icon className="w-2.5 h-2.5" />
      {artifact.name}
    </button>
  );
};

const ThinkingTrail = ({ text, isDark }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`mt-2 border-t ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} pt-2`}>
      <button onClick={() => setOpen(!open)} className={`flex items-center gap-1 text-[10px] font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`}>
        <Brain className="w-2.5 h-2.5" />
        {open ? 'Hide reasoning' : 'Why this?'}
        <ChevronDown className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className={`mt-1.5 text-[10px] leading-relaxed ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{text}</p>}
    </div>
  );
};

/* ────── Risk Resolution Card ────── */
const RiskCard = ({ item, isDark, status, onResolve }) => {
  const [expanded, setExpanded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isGone, setIsGone] = useState(false);

  const isDone = status === 'resolved';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const defaultBorder = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const agentThumb = AGENT_THUMBS[item.agents?.[0]];

  const cardBg = isDone
    ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50')
    : (isDark ? 'bg-red-950/20' : 'bg-red-50/60');
  const border = isDone
    ? (isDark ? 'border-emerald-500/20' : 'border-emerald-200/60')
    : (isDark ? 'border-red-500/15' : 'border-red-200/60');

  useEffect(() => {
    if (isDone) {
      const t1 = setTimeout(() => setIsExiting(true), 1500);
      const t2 = setTimeout(() => setIsGone(true), 2200);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [isDone]);

  if (isGone) return null;

  return (
    <div
      className={`${cardBg} border ${border} rounded-lg px-3 py-2.5 transition-all duration-600 ${isExiting ? 'opacity-0 max-h-0 overflow-hidden py-0 my-0 scale-95' : 'opacity-100 max-h-[600px]'}`}
      data-testid={`briefing-card-${item.id}`}
    >
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${isDone ? (isDark ? 'text-white/50 line-through' : 'text-slate-500 line-through') : textPrimary}`}>{item.title}</p>
        {isDone && (
          <span className="flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full text-emerald-400 bg-emerald-400/15">
            <Check className="w-2.5 h-2.5" /> Resolved
          </span>
        )}
        {!isDone && (
          <>
            {item.severity === 'critical' && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-600/10'}`}>Critical</span>}
            {item.severity === 'high' && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? 'text-amber-400 bg-amber-400/10' : 'text-orange-700 bg-orange-700/10'}`}>High Risk</span>}
          </>
        )}
      </div>

      <p className={`text-[11px] mt-1 line-clamp-1 ml-7 ${textSecondary}`}>{item.description}</p>

      {!isDone && (
        <div className="flex items-center gap-1.5 mt-2 ml-7">
          <button onClick={() => onResolve?.(item.id)} className="flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-500/20 text-indigo-400 text-[10px] font-medium hover:bg-indigo-500/30 transition-colors" data-testid={`resolve-${item.id}`}>
            <Zap className="w-2.5 h-2.5" /> Resolve
          </button>
          {item.expanded && (
            <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${defaultBorder} ${textSecondary} ${hoverBg}`} data-testid={`review-${item.id}`}>
              <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
            </button>
          )}
        </div>
      )}

      {expanded && !isDone && item.expanded && (
        <div className="mt-3 ml-7 space-y-2">
          {item.expanded.summary && <p className={`text-[10px] leading-relaxed ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{item.expanded.summary}</p>}
          {item.expanded.details && (
            <div className={`rounded-md overflow-hidden border ${defaultBorder}`}>
              {item.expanded.details.map((d, i) => (
                <div key={i} className={`flex items-center justify-between px-2.5 py-1.5 text-[10px] ${i % 2 === 0 ? (isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]') : ''}`}>
                  <span className={textSecondary}>{d.label}</span>
                  <span className={`font-medium ${textPrimary}`}>{d.value}</span>
                </div>
              ))}
            </div>
          )}
          {item.expanded.proposedActions && (
            <div className={`mt-2 border-t ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} pt-2`}>
              <p className={`text-[10px] font-medium mb-1.5 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>Proposed resolution</p>
              <div className="space-y-1">
                {item.expanded.proposedActions.map((a, i) => (
                  <div key={i} className={`flex items-center gap-1.5 text-[10px] ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                    <ArrowRight className="w-2.5 h-2.5 flex-shrink-0" />
                    <span>{a.label}</span>
                    <span className={`${isDark ? 'text-white/30' : 'text-slate-500'}`}>— {a.agent}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {item.expanded.thinking && <ThinkingTrail text={item.expanded.thinking} isDark={isDark} />}
        </div>
      )}
    </div>
  );
};

/* ────── Approval Card (disappears after action) ────── */
const ApprovalCard = ({ item, isDark, status, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isGone, setIsGone] = useState(false);

  const isDone = status === 'approved' || status === 'rejected';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const defaultBorder = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const agentThumb = AGENT_THUMBS[item.agent];

  const cardBg = status === 'approved'
    ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50')
    : status === 'rejected'
      ? (isDark ? 'bg-red-950/15' : 'bg-red-50/50')
      : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = status === 'approved'
    ? (isDark ? 'border-emerald-500/20' : 'border-emerald-200/60')
    : status === 'rejected'
      ? (isDark ? 'border-red-500/20' : 'border-red-200/60')
      : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');

  useEffect(() => {
    if (isDone) {
      const t1 = setTimeout(() => setIsExiting(true), 1200);
      const t2 = setTimeout(() => setIsGone(true), 1800);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [isDone]);

  if (isGone) return null;

  return (
    <div
      className={`${cardBg} border ${border} rounded-lg px-3 py-2.5 transition-all duration-500 ${isExiting ? 'opacity-0 max-h-0 overflow-hidden py-0 my-0 scale-95' : 'opacity-100 max-h-[500px]'}`}
      data-testid={`briefing-card-${item.id}`}
    >
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{item.title}</p>
        {status === 'approved' && (
          <span className="flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full text-emerald-400 bg-emerald-400/15">
            <Check className="w-2.5 h-2.5" /> Approved
          </span>
        )}
        {status === 'rejected' && (
          <span className="flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full text-red-400 bg-red-400/15">
            <X className="w-2.5 h-2.5" /> Rejected
          </span>
        )}
      </div>

      {!isDone && (
        <>
          <p className={`text-[11px] mt-1 line-clamp-1 ml-7 ${textSecondary}`}>{item.description}</p>
          {item.amount && <p className={`text-[11px] font-medium mt-1 ml-7 ${textPrimary}`}>{item.type}: {item.amount}</p>}

          <div className="flex items-center gap-1.5 mt-2 ml-7">
            <button onClick={() => onApprove?.(item.id)} className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-400 text-[10px] font-medium hover:bg-emerald-500/30 transition-colors" data-testid={`approve-${item.id}`}>
              <Check className="w-2.5 h-2.5" /> Approve
            </button>
            <button onClick={() => onReject?.(item.id)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${defaultBorder} ${textSecondary} hover:bg-red-500/10 hover:text-red-400 transition-colors`} data-testid={`reject-${item.id}`}>
              <X className="w-2.5 h-2.5" /> Reject
            </button>
            {item.expanded && (
              <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${defaultBorder} ${textSecondary} ${hoverBg}`} data-testid={`review-${item.id}`}>
                <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
              </button>
            )}
          </div>
        </>
      )}

      {expanded && !isDone && item.expanded && (
        <div className="mt-3 ml-7 space-y-2">
          {item.expanded.summary && <p className={`text-[10px] leading-relaxed ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{item.expanded.summary}</p>}
          {item.expanded.details && (
            <div className={`rounded-md overflow-hidden border ${defaultBorder}`}>
              {item.expanded.details.map((d, i) => (
                <div key={i} className={`flex items-center justify-between px-2.5 py-1.5 text-[10px] ${i % 2 === 0 ? (isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]') : ''}`}>
                  <span className={textSecondary}>{d.label}</span>
                  <span className={`font-medium ${textPrimary}`}>{d.value}</span>
                </div>
              ))}
            </div>
          )}
          {item.expanded.artifacts && (
            <div>
              <p className={`text-[10px] font-medium mb-1.5 ${textSecondary}`}>Attachments</p>
              <div className="flex flex-wrap gap-1.5">{item.expanded.artifacts.map((a, i) => <ArtifactChip key={i} artifact={a} isDark={isDark} />)}</div>
            </div>
          )}
          {item.expanded.thinking && <ThinkingTrail text={item.expanded.thinking} isDark={isDark} />}
        </div>
      )}
    </div>
  );
};

/* ────── Must-Do Card ────── */
const MustDoCard = ({ item, isDark, status, onBeginTask }) => {
  const [expanded, setExpanded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isGone, setIsGone] = useState(false);

  const isDone = status === 'completed';
  const isActive = status === 'in-progress';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const defaultBorder = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const agentThumb = AGENT_THUMBS[item.agent];

  const cardBg = isDone
    ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50')
    : isActive
      ? (isDark ? 'bg-indigo-950/20' : 'bg-indigo-50/50')
      : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = isDone
    ? (isDark ? 'border-emerald-500/20' : 'border-emerald-200/60')
    : isActive
      ? (isDark ? 'border-indigo-500/20' : 'border-indigo-200/60')
      : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');

  useEffect(() => {
    if (isDone) {
      const t1 = setTimeout(() => setIsExiting(true), 1500);
      const t2 = setTimeout(() => setIsGone(true), 2200);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [isDone]);

  if (isGone) return null;

  return (
    <div
      className={`${cardBg} border ${border} rounded-lg px-3 py-2.5 transition-all duration-500 ${isExiting ? 'opacity-0 max-h-0 overflow-hidden py-0 my-0 scale-95' : 'opacity-100 max-h-[500px]'}`}
      data-testid={`briefing-card-${item.id}`}
    >
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${isDone ? (isDark ? 'text-white/50 line-through' : 'text-slate-500 line-through') : textPrimary}`}>{item.title}</p>
        {isDone && (
          <span className="flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full text-emerald-400 bg-emerald-400/15">
            <Check className="w-2.5 h-2.5" /> Completed
          </span>
        )}
        {isActive && (
          <span className="flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full text-indigo-400 bg-indigo-400/15">
            <Loader2 className="w-2.5 h-2.5 animate-spin" /> In Progress
          </span>
        )}
        {!isDone && !isActive && item.urgency && <UrgencyBadge urgency={item.urgency} isDark={isDark} />}
      </div>

      <p className={`text-[11px] mt-1 line-clamp-1 ml-7 ${textSecondary}`}>{item.description}</p>

      <div className="flex items-center gap-1.5 mt-2 ml-7">
        {status === 'pending' && (
          <button onClick={() => onBeginTask?.(item.id)} className="flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-500/20 text-indigo-400 text-[10px] font-medium hover:bg-indigo-500/30 transition-colors" data-testid={`begin-task-${item.id}`}>
            <Play className="w-2.5 h-2.5" /> Begin Task
          </button>
        )}
        {isActive && (
          <span className={`flex items-center gap-1 text-[10px] ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
            <Loader2 className="w-2.5 h-2.5 animate-spin" /> Working with Luc...
          </span>
        )}
        {!isDone && !isActive && item.expanded && (
          <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${defaultBorder} ${textSecondary} ${hoverBg}`} data-testid={`review-${item.id}`}>
            <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
          </button>
        )}
      </div>

      {expanded && !isDone && !isActive && item.expanded && (
        <div className="mt-3 ml-7 space-y-2">
          {item.expanded.summary && <p className={`text-[10px] leading-relaxed ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{item.expanded.summary}</p>}
          {item.expanded.details && (
            <div className={`rounded-md overflow-hidden border ${defaultBorder}`}>
              {item.expanded.details.map((d, i) => (
                <div key={i} className={`flex items-center justify-between px-2.5 py-1.5 text-[10px] ${i % 2 === 0 ? (isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]') : ''}`}>
                  <span className={textSecondary}>{d.label}</span>
                  <span className={`font-medium ${textPrimary}`}>{d.value}</span>
                </div>
              ))}
            </div>
          )}
          {item.expanded.artifacts && (
            <div>
              <p className={`text-[10px] font-medium mb-1.5 ${textSecondary}`}>Attachments</p>
              <div className="flex flex-wrap gap-1.5">{item.expanded.artifacts.map((a, i) => <ArtifactChip key={i} artifact={a} isDark={isDark} />)}</div>
            </div>
          )}
          {item.expanded.proposedActions && (
            <div className={`mt-2 border-t ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} pt-2`}>
              <p className={`text-[10px] font-medium mb-1.5 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>Proposed resolution</p>
              <div className="space-y-1">
                {item.expanded.proposedActions.map((a, i) => (
                  <div key={i} className={`flex items-center gap-1.5 text-[10px] ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                    <ArrowRight className="w-2.5 h-2.5 flex-shrink-0" />
                    <span>{a.label}</span>
                    <span className={`${isDark ? 'text-white/30' : 'text-slate-500'}`}>— {a.agent}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {item.expanded.thinking && <ThinkingTrail text={item.expanded.thinking} isDark={isDark} />}
        </div>
      )}
    </div>
  );
};

/* ────── Meeting Item ────── */
const MeetingItem = ({ meeting, isDark, meetingStatus, notes }) => {
  const [expanded, setExpanded] = useState(false);
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const hasAgenda = meeting.agenda && meeting.agenda.length > 0;
  const isInternal = meeting.type === 'internal';
  const statusConfig = MEETING_STATUS_LABELS[meetingStatus];

  const bg = meetingStatus === 'completed'
    ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/40')
    : meetingStatus === 'in-progress'
      ? (isDark ? 'bg-indigo-950/15' : 'bg-indigo-50/40')
      : meetingStatus === 'reminded'
        ? (isDark ? 'bg-amber-950/15' : 'bg-amber-50/40')
        : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');

  const borderColor = meetingStatus === 'completed'
    ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/50')
    : meetingStatus === 'in-progress'
      ? (isDark ? 'border-indigo-500/15' : 'border-indigo-200/50')
      : meetingStatus === 'reminded'
        ? (isDark ? 'border-amber-500/15' : 'border-amber-200/50')
        : border;

  return (
    <div className={`${bg} border ${borderColor} rounded-lg px-3 py-2 transition-all duration-500`} data-testid={`meeting-${meeting.id}`}>
      <div className="flex items-center gap-3">
        <div className={`text-[11px] font-mono font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-600'} w-16 flex-shrink-0`}>{meeting.time}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-xs font-medium truncate ${textPrimary}`}>{meeting.title}</p>
            {statusConfig && (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? statusConfig.colorDark : statusConfig.colorLight}`}>
                {meetingStatus === 'in-progress' && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
                {meetingStatus === 'completed' && <Check className="w-2.5 h-2.5" />}
                {meetingStatus === 'reminded' && <Clock className="w-2.5 h-2.5" />}
                {statusConfig.label}
              </span>
            )}
          </div>
          <p className={`text-[10px] ${textSecondary}`}>{meeting.with} · {meeting.duration}</p>
        </div>
        <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg} flex-shrink-0`} data-testid={`meeting-expand-${meeting.id}`}>
          <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 ml-[76px]">
          {notes && (
            <div className={`mb-2 p-2 rounded-md ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-500/8'}`}>
              <p className={`text-[10px] font-medium mb-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Meeting Notes (by Luc)</p>
              <p className={`text-[10px] leading-relaxed ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{notes}</p>
            </div>
          )}
          {hasAgenda ? (
            <div className="mb-2">
              <p className={`text-[10px] font-medium mb-1 ${textSecondary}`}>Agenda</p>
              <div className="space-y-1">
                {meeting.agenda.map((item, i) => (
                  <div key={i} className={`flex items-start gap-1.5 text-[11px] ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                    <span className={`${textSecondary} flex-shrink-0`}>{i + 1}.</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-2">
              <p className={`text-[11px] italic ${textSecondary}`}>No agenda set</p>
              {isInternal && <button className="flex items-center gap-1 mt-1.5 px-2 py-1 rounded-md text-[10px] font-medium bg-indigo-500/20 text-indigo-400" data-testid={`meeting-request-agenda-${meeting.id}`}><ClipboardList className="w-2.5 h-2.5" /> Request Agenda</button>}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <button className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`} data-testid={`meeting-reschedule-${meeting.id}`}><RefreshCw className="w-2.5 h-2.5" /> Reschedule</button>
            <button className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`} data-testid={`meeting-notes-${meeting.id}`}><FileText className="w-2.5 h-2.5" /> Meeting Notes</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ────── Follow-up Task ────── */
const FollowUpTaskCard = ({ task, isDark, onBeginTask }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const agentThumb = AGENT_THUMBS[task.agent];
  return (
    <div className={`border rounded-lg px-3 py-2.5 ${isDark ? 'bg-purple-950/15 border-purple-500/20' : 'bg-purple-50/50 border-purple-200/60'}`} style={{ animation: 'taskSlideIn 0.4s ease-out' }} data-testid={`follow-up-${task.id}`}>
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{task.title}</p>
        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'text-purple-400 bg-purple-400/10' : 'text-purple-600 bg-purple-600/10'}`}>Follow-up</span>
      </div>
      <p className={`text-[11px] mt-1 ml-7 ${textSecondary}`}>{task.description}</p>
      <div className="flex items-center gap-2 mt-2 ml-7">
        {task.urgency && <UrgencyBadge urgency={task.urgency} isDark={isDark} />}
        <button
          onClick={() => onBeginTask?.(task.id)}
          className="flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-500/20 text-indigo-400 text-[10px] font-medium hover:bg-indigo-500/30 transition-colors"
          data-testid={`begin-followup-${task.id}`}
        >
          <Play className="w-2.5 h-2.5" /> Begin Task
        </button>
      </div>
    </div>
  );
};

/* ────── Interactive Inbox Item ────── */
const InteractiveInboxItem = ({ email, isDark, status, onReply }) => {
  const [expanded, setExpanded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isGone, setIsGone] = useState(false);
  const isDone = !!status;
  const cardBg = isDone ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = isDone ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/50') : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';

  useEffect(() => {
    if (isDone) {
      const t1 = setTimeout(() => setIsExiting(true), 1200);
      const t2 = setTimeout(() => setIsGone(true), 1800);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [isDone]);

  if (isGone) return null;

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2 transition-all duration-500 ${isExiting ? 'opacity-0 max-h-0 overflow-hidden py-0 my-0 scale-95' : 'opacity-100 max-h-[300px]'}`} data-testid={`inbox-item-${email.id}`}>
      <div className="flex items-center gap-2">
        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold ${isDark ? 'bg-white/[0.08] text-white/70' : 'bg-black/[0.06] text-slate-600'}`}>{email.senderInitials}</div>
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{email.sender}</p>
        <span className={`text-[9px] whitespace-nowrap ${textSecondary}`}>{email.time}</span>
      </div>
      <p className={`text-[11px] mt-1 ml-7 truncate ${textSecondary}`}>{email.summary}</p>
      {!isDone && (
        <div className="flex items-center gap-1.5 mt-1.5 ml-7">
          <button onClick={() => onReply?.(email.id)} className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors" data-testid={`inbox-reply-${email.id}`}>
            Reply
          </button>
          <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`} data-testid={`inbox-more-${email.id}`}>
            {expanded ? 'Less' : 'More'}
          </button>
        </div>
      )}
      {isDone && (
        <div className="ml-7 mt-1">
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}>
            <Check className="w-2.5 h-2.5 inline mr-0.5" />{status === 'sent' ? 'Replied' : status === 'drafted' ? 'Drafted' : 'Reminded'}
          </span>
        </div>
      )}
      {expanded && !isDone && (
        <div className={`mt-2 ml-7 p-2.5 rounded-md ${isDark ? 'bg-white/[0.03]' : 'bg-black/[0.02]'}`}>
          <p className={`text-[11px] leading-relaxed ${textSecondary}`}>{email.expandedSummary}</p>
        </div>
      )}
    </div>
  );
};

/* ────── Main Component ────── */
const V4CeoBriefing = ({ isDark, simState, simClock, onFullscreenChange, ripples, onRippleComplete, onRippleAck }) => {
  const data = CEO_BRIEFING;
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';

  const [activeTask, setActiveTask] = useState(null);
  const [activeRisk, setActiveRisk] = useState(null);
  const [riskPhase, setRiskPhase] = useState('briefing'); // 'briefing' | 'executing'
  const [activeEmail, setActiveEmail] = useState(null);

  const isFullscreen = !!(activeTask || activeRisk || activeEmail);
  useEffect(() => {
    onFullscreenChange?.(isFullscreen);
  }, [isFullscreen, onFullscreenChange]);

  const statuses = simState?.statuses || {};
  const followUpTasks = simState?.followUpTasks || [];
  const meetingNotes = simState?.meetingNotes || {};
  const pendingApprovals = simState?.pendingApprovals ?? data.approvals.length;
  const pendingRisks = simState?.pendingRisks ?? data.risks.length;
  const inboxStatus = simState?.inboxStatus || {};
  const pendingEmails = simState?.pendingEmails || data.inbox;
  const allEmails = simState?.allEmails || data.inbox;
  const allReplies = { ...EMAIL_REPLIES, ...INCOMING_EMAIL_REPLIES };

  const handleBeginTask = (taskId) => {
    simState?.startTask(taskId);
    setActiveTask(taskId);
  };

  const handleTaskComplete = () => {
    if (activeTask) simState?.completeTask(activeTask);
  };

  const handleBackFromTask = () => {
    setActiveTask(null);
  };

  const handleResolveRisk = (riskId) => {
    setActiveRisk(riskId);
    setRiskPhase('briefing');
  };

  const handleBackFromRisk = () => {
    if (riskPhase === 'executing') {
      // Coming back from execution means risk is resolved
      if (activeRisk) simState?.resolveRisk(activeRisk);
    }
    setActiveRisk(null);
    setRiskPhase('briefing');
  };

  const handleExecuteRisk = () => {
    setRiskPhase('executing');
  };

  const RISK_EXEC_MAP = {
    r1: { data: R1_EXECUTION, share: R1_SHARE_DETAILS, changes: R1_REQUEST_CHANGE_RESPONSES, messages: R1_CHANGE_MESSAGES },
    r2: { data: R2_EXECUTION, share: R2_SHARE_DETAILS, changes: R2_REQUEST_CHANGE_RESPONSES, messages: R2_CHANGE_MESSAGES },
  };

  const handleReplyEmail = (emailId) => {
    setActiveEmail(emailId);
  };

  const handleEmailAction = (emailId, action) => {
    simState?.markEmailDone(emailId, action);
  };

  const handleEmailBack = (type, nextEmailId) => {
    if (type === 'next' && nextEmailId) {
      setActiveEmail(nextEmailId);
    } else {
      setActiveEmail(null);
    }
  };

  // Email reply mode
  if (activeEmail) {
    const emailData = allEmails.find(e => e.id === activeEmail);
    return (
      <V4EmailReply
        isDark={isDark}
        emailId={activeEmail}
        emailData={emailData}
        onBack={handleEmailBack}
        onEmailHandled={handleEmailAction}
        pendingEmails={pendingEmails}
        allReplies={allReplies}
      />
    );
  }

  // Risk resolution — Phase 1: Stark briefing, Phase 2: Execution
  if (activeRisk) {
    if (riskPhase === 'executing') {
      const riskExec = RISK_EXEC_MAP[activeRisk];
      if (riskExec) {
        return (
          <V4ExecutionView
            isDark={isDark}
            onBack={handleBackFromRisk}
            executionData={riskExec.data}
            shareDetailsData={riskExec.share}
            requestChangeData={riskExec.changes}
            requestChangeMessagesData={riskExec.messages}
            backLabel="Back to Briefing"
          />
        );
      }
    }
    return (
      <V4RiskBriefing
        isDark={isDark}
        riskId={activeRisk}
        onBack={handleBackFromRisk}
        onExecute={handleExecuteRisk}
      />
    );
  }

  // Task execution mode
  if (activeTask) {
    return (
      <V4TaskExecution
        isDark={isDark}
        taskId={activeTask}
        onBack={handleBackFromTask}
        onComplete={handleTaskComplete}
        simState={simState}
      />
    );
  }

  return (
    <div className="flex flex-col h-full px-5 py-4" data-testid="ceo-briefing">
      <style>{`
        @keyframes taskSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Greeting */}
      <div className="mb-4 flex-shrink-0">
        <div className="flex gap-3">
          <img src={AGENT_THUMBS.luc} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5" />
          <div>
            <p className={`text-[15px] ${isDark ? 'text-white/70' : 'text-slate-700'} leading-relaxed`}>
              Good morning, Arjun. Hope the weekend treated you well! You've got a solid day ahead — 2 risks need early attention, but your pipeline is looking strong at 3.2x. I've lined everything up for you.
            </p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className={`text-[11px] flex items-center gap-1 ${textSecondary}`}><Calendar className="w-3 h-3" /> {data.greeting.meetings} meetings</span>
              <span className={`text-[11px] flex items-center gap-1 ${pendingApprovals === 0 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : textSecondary}`}><Check className="w-3 h-3" /> {pendingApprovals === 0 ? 'All approved' : `${pendingApprovals} approvals`}</span>
              <span className={`text-[11px] flex items-center gap-1 ${pendingRisks === 0 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : 'text-red-400'}`}><AlertTriangle className="w-3 h-3" /> {pendingRisks === 0 ? 'Risks resolved' : `${pendingRisks} risks flagged`}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-3 gap-5 flex-1 min-h-0" data-tour="briefing-cols">
        {/* Column 1: Inbox + Meetings */}
        <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
          <SectionLabel icon={Mail} title="Inbox Snapshot" count={pendingEmails.length} isDark={isDark} />
          {pendingEmails.length === 0 ? (
            <div className={`flex flex-col items-center py-6 px-3 rounded-lg border ${isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-black/[0.06] bg-black/[0.01]'}`}>
              <Check className={`w-5 h-5 mb-1.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <p className={`text-[11px] font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Priority inbox clear</p>
              <p className={`text-[10px] mt-0.5 text-center ${textSecondary}`}>All emails handled</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-0.5">
              {allEmails.map((email) => (
                <InteractiveInboxItem key={email.id} email={email} isDark={isDark} status={inboxStatus[email.id]} onReply={handleReplyEmail} />
              ))}
              {(ripples?.emails || []).map((email) => (
                <RippledInboxItem key={email.id} email={email} isDark={isDark} onHandle={onRippleAck ? (id) => onRippleAck(id, 'handled') : undefined} />
              ))}
            </div>
          )}
          <div className="pt-4">
            <SectionLabel icon={Calendar} title="Meetings" count={data.meetings.length} isDark={isDark} />
            <div className="flex flex-col gap-2">
              {data.meetings.map((m) => (
                <MeetingItem key={m.id} meeting={m} isDark={isDark} meetingStatus={statuses.meetings?.[m.id] || 'upcoming'} notes={meetingNotes[m.id]} />
              ))}
            </div>
          </div>
        </div>

        {/* Column 2: Risks + Must-Do */}
        <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
          <SectionLabel icon={AlertTriangle} title="Risks Flagged" count={pendingRisks + (ripples?.risks?.length || 0)} isDark={isDark} accent="red" />
          <div className="flex flex-col gap-3">
            {data.risks.map((item) => (
              <RiskCard key={item.id} item={item} isDark={isDark} status={statuses.risks?.[item.id] || 'pending'} onResolve={handleResolveRisk} />
            ))}
            {(ripples?.risks || []).map((item) => (
              <RippledRiskCard key={item.id} item={item} isDark={isDark} onResolve={onRippleAck ? (id) => onRippleAck(id, 'resolved') : undefined} />
            ))}
          </div>

          <div className="pt-4">
            <SectionLabel icon={Zap} title="Must-Do" count={data.priorities.mustDo.length + followUpTasks.length + (ripples?.tasks?.length || 0)} isDark={isDark} accent="amber" />
            <div className="flex flex-col gap-3">
              {data.priorities.mustDo.map((item) => (
                <MustDoCard key={item.id} item={item} isDark={isDark} status={statuses.mustDo?.[item.id] || 'pending'} onBeginTask={handleBeginTask} />
              ))}
              {followUpTasks.map((task) => <FollowUpTaskCard key={task.id} task={task} isDark={isDark} onBeginTask={handleBeginTask} />)}
              {(ripples?.tasks || []).map((task) => (
                <RippledTaskCard key={task.id} task={task} isDark={isDark} onComplete={onRippleComplete} />
              ))}
            </div>
          </div>
        </div>

        {/* Column 3: Approvals */}
        <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
          <SectionLabel icon={Check} title="Pending Approvals" count={pendingApprovals + (ripples?.approvals?.length || 0)} isDark={isDark} />
          <div className="flex flex-col gap-3">
            {data.approvals.map((item) => (
              <ApprovalCard key={item.id} item={item} isDark={isDark} status={statuses.approvals?.[item.id] || 'pending'} onApprove={simState?.approveItem} onReject={simState?.rejectItem} />
            ))}
            {(ripples?.approvals || []).map((item) => (
              <RippledApprovalCard key={item.id} item={item} isDark={isDark} onApprove={(id) => { onRippleAck?.(id, 'approved'); onRippleComplete?.(id); }} onReject={(id) => onRippleAck?.(id, 'rejected')} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default V4CeoBriefing;
